import React, { useRef, useEffect, useState, useContext } from 'react';
import { ActionIcon, Alert, Badge, Box, Button, Flex, Group, Indicator, NumberInput, Popover, Stack, TextInput, Tooltip, UnstyledButton } from "@mantine/core"
import * as d3 from 'd3';
import { getHardcodedColor } from '../../utils';
import { CiLocationArrow1 } from 'react-icons/ci';
import { MdOutlineNearMeDisabled } from 'react-icons/md';
import DataContext from '../../providers/DataProvider';
import { roundToDecimals, toPercentage } from '../../utils'
import { FaRegCheckSquare, FaRegClone, FaRegSquare, FaRegTrashAlt } from 'react-icons/fa';
import UserSelectionContext from '../../providers/UserSelectionProvider';
import { RiEqualizer3Line } from 'react-icons/ri';
import { BiErrorCircle } from 'react-icons/bi';
import { dataStreamTypes } from '../../types';

interface VerticalBarChartProp {
    data: { [key: string]: number };
    draggable?: boolean
    hasLabels?: boolean
    transparent?: boolean
    width: number
    height: number
    onWeightsSelected?: (data: { [key: string]: number }) => void
}

const VerticalBarChart: React.FC<VerticalBarChartProp> = ({ data, width, height, draggable = false, hasLabels = false,
    transparent = false, onWeightsSelected = undefined }) => {
    const svgRef = useRef();

    useEffect(() => {
        const weights = Object.entries(data)

        if (onWeightsSelected) onWeightsSelected(Object.fromEntries(weights))

        const margin = { top: 0, right: 5, bottom: 0, left: 0 };
        width = width - margin.left - margin.right;
        height = height - margin.top - margin.bottom;

        const svg = d3.select(svgRef.current)
            .attr('width', width + margin.left + margin.right)
            .attr('height', height + margin.top + margin.bottom)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        const x = d3.scaleBand()
            .domain(weights.map(w => w[0]))
            .range([0, width])
            .padding(0);

        const y = d3.scaleLinear()
            .domain([0, 1.2])
            .nice()
            .range([height, 0]);

        const bars = svg.selectAll('.bar')
            .data(weights)
            .enter()
            .append('rect')
            .attr('class', 'bar')
            .attr('x', w => x(w[0]))
            .attr('y', w => y(w[1]))
            .attr('width', x.bandwidth())
            .attr('height', w => height - y(w[1]))
            .attr('fill', w => getHardcodedColor(w[0]))
            .attr('opacity', transparent ? 0.4 : 1);

        let labels = null
        if (hasLabels)
            labels = svg.selectAll('.label')
                .data(weights)
                .enter()
                .append('text')
                .attr('class', 'label')
                .attr('x', w => x(w[0]) + x.bandwidth() / 2)
                .attr('y', w => y(w[1]) - 5)
                .attr('text-anchor', 'middle')
                .attr('font-size', '70%')
                .attr('fill', 'black')
                .text(w => toPercentage(w[1]));

        const drag = d3.drag()
            .on('drag', function (event, w, k) {
                const newValue = roundToDecimals(Math.max(0, Math.min(1, y.invert(event.y))), 2);
                w[1] = newValue;

                svg.selectAll('.bar')
                    .data(weights)
                    .attr('y', w => y(w[1]))
                    .attr('height', w => height - y(w[1]));

                svg.selectAll('.label')
                    .data(weights)
                    .attr('y', w => y(w[1]) - 5)
                    .text(w => toPercentage(w[1]));
            })
            .on('end', () => {
                if (onWeightsSelected) onWeightsSelected(Object.fromEntries(weights))
            })

        if (draggable)
            if (hasLabels) labels.call(drag);
            else bars.call(drag)

        return () => {
            d3.select(svgRef.current).selectAll('*').remove();
        };
    }, [data]);

    return <svg style={{ cursor: 'pointer' }} ref={svgRef} />;
};

const AjustableWeights = ({ wkey }) => {
    const [opened, setOpened] = useState(false);
    const { updateWeights, cloneWeights, removeWeights, weights, maxWeights, selectedRun } = useContext(DataContext)
    const { selectedWeights, setSelectedWeights } = useContext(UserSelectionContext)
    const [currentWeights, setCurrentWeights] = useState<any>(weights[selectedRun.run_id][wkey])
    const [weightsName, setWeightsName] = useState(wkey)

    const isValid = () => roundToDecimals(Object.values(currentWeights).reduce((a, c) => a + c, 0), 2) == 1

    useEffect(() => {
        if (selectedWeights === wkey) {
            setWeightsName(wkey);
        }
    }, [selectedWeights]);

    return (
        <Popover opened={opened} position="bottom" trapFocus withArrow shadow="lg"
            onClose={() => {
                setCurrentWeights(weights[selectedRun.run_id][wkey])
                setOpened(false)
            }}
        >
            <Popover.Target>
                <Box style={{ height: '100px' }} onClick={() => setOpened(prev => !prev)}>
                    <VerticalBarChart data={Object.values(dataStreamTypes).reduce((acc, key) => {
                        if (key in weights[selectedRun.run_id][wkey]) acc[key] = weights[selectedRun.run_id][wkey][key]
                        return acc
                    }, {})}
                        width={100} height={100} draggable={false} transparent={wkey !== selectedWeights} />
                </Box>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack align='center'>
                    {!isValid() &&
                        <Alert variant="light" color="red" icon={<BiErrorCircle />}>
                            Weights should sum up to 100%.
                        </Alert>}
                    <Group gap={0} style={{ width: '100%' }}>
                    <Flex
                        style={{ width: '50%' }}
                        justify='flex-start'
                        align="flex-start"
                        direction="row"
                        wrap="wrap"
                        gap='xs'
                    >
                        <Tooltip label={"Select"} position="top">
                            <ActionIcon
                                onClick={() => { setSelectedWeights(wkey) }}
                                variant='transparent'
                            >
                                {wkey === selectedWeights ? <FaRegCheckSquare /> : <FaRegSquare />}
                            </ActionIcon>
                        </Tooltip>
                    </Flex>
                    <Flex
                        style={{ width: '50%' }}
                        justify='flex-end'
                        align="flex-end"
                        direction="row"
                        wrap="wrap"
                        gap='xs'
                    >
                        {wkey === 'A' && <Badge variant='filled' size='sm'>Default</Badge>}
                    </Flex>
                    </Group>
                    {/* <TextInput
                        label="Name"
                        value={weightsName}
                        onChange={(e) => setWeightsName(e.currentTarget.value)}
                    /> */}
                    <VerticalBarChart
                        data={Object.values(dataStreamTypes).reduce((acc, key) => {
                            if (key in currentWeights) acc[key] = currentWeights[key]
                            return acc
                        }, {})} width={5 * 65 + 10} height={100}
                        draggable={false} hasLabels={true}
                        transparent={wkey !== selectedWeights} />
                    <Group gap={1}>
                        {Object.entries(Object.values(dataStreamTypes).reduce((acc, key) => {
                            if (key in weights[selectedRun.run_id][wkey]) acc[key] = weights[selectedRun.run_id][wkey][key]
                            return acc
                        }, {})).map(([k, v]) =>
                            <NumberInput
                                min={0}
                                max={100}
                                suffix="%"
                                value={roundToDecimals(currentWeights[k] * 100, 2)}
                                onChange={(value) => { setCurrentWeights(prev => ({ ...prev, [k]: value / 100 })) }}
                                allowNegative={true}
                                allowDecimal={true}
                                size='xs'
                                style={{ width: 65 }}
                                styles={{
                                    input: {
                                        borderColor: getHardcodedColor(k)
                                    }
                                }}
                                disabled={wkey === 'A'}
                            />
                        )}
                    </Group>
                    <Group gap={0} style={{ width: '100%' }}>
                        <Flex
                            style={{ width: '50%' }}
                            justify='flex-start'
                            align="flex-start"
                            direction="row"
                            gap='xs'
                        >
                            <Tooltip label="Delete" position="top">
                                <ActionIcon
                                    color="red"
                                    disabled={wkey === 'A'} // TODO: refactor
                                    onClick={() => {
                                        removeWeights(selectedRun.run_id, wkey)
                                        setOpened(false)
                                    }}
                                    variant='transparent'
                                >
                                    <FaRegTrashAlt />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Clone" position="top">
                                <ActionIcon
                                    disabled={Object.keys(weights[selectedRun.run_id]).filter(k => !k.startsWith('alpha')).length === maxWeights}
                                    variant='transparent'
                                    onClick={() => {
                                        cloneWeights(selectedRun.run_id, wkey)
                                        setOpened(false)
                                    }}
                                >
                                    <FaRegClone />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Re-distribute" position="top">
                                <ActionIcon
                                    variant='transparent'
                                    onClick={() => {
                                        const sum = Object.values(currentWeights).reduce((p, c) => p + c, 0) * 100;
                                        setCurrentWeights(prev => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, v * 100 / sum])))
                                    }}
                                >
                                    <RiEqualizer3Line />
                                </ActionIcon>
                            </Tooltip>
                        </Flex>
                        <Flex
                            style={{ width: '50%' }}
                            justify='flex-end'
                            align="flex-end"
                            direction="row"
                            gap='xs'>
                            {wkey !== 'A' && <Button
                                style={{ cursor: isValid() ? undefined : 'not-allowed' }}
                                variant='transparent'
                                color={isValid() ? undefined : 'red'}
                                onClick={isValid() ? () => {
                                    updateWeights(selectedRun.run_id, wkey, weightsName, currentWeights)
                                    setOpened(false)
                                } : undefined
                                }
                                leftSection={isValid() ? <CiLocationArrow1 /> : <MdOutlineNearMeDisabled />}
                            >
                                Apply
                            </Button>}
                        </Flex>
                    </Group>
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
}

interface VerticalBarChartHeaderProp {
    data: { [key: string]: number };
    run_id: string
}

const VerticalBarChartHeader: React.FC<VerticalBarChartHeaderProp> = ({ data, run_id }) => {
    const { weights, selectedRun } = useContext(DataContext)
    const { selectedWeights } = useContext(UserSelectionContext)
    let i = 1
    const weightsN = Object.keys(weights[selectedRun.run_id]).filter(k => k !== selectedWeights && !k.startsWith('alpha')).length
    return (
        <Group align="flex-end" style={{ width: '100%' }} gap={5}>
            <Indicator inline label={selectedWeights} color='#82878b' position='middle-center' size={16} radius={0} disabled={weightsN === 0}>
                <AjustableWeights wkey={selectedWeights} />
            </Indicator>
            {Object.keys(weights[selectedRun.run_id]).map((k) => {
                if (k !== selectedWeights && !k.startsWith('alpha'))
                    return (
                        <Indicator inline label={k} color='gray.4' position='middle-center' size={16} radius={0}>
                            <AjustableWeights wkey={k} />
                        </Indicator>
                    )
            }
            )}
        </Group>
    );
};

export default VerticalBarChartHeader;
